import React from 'react'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import fullName from '../../utils/fullName'
import {
  PaginationResponse,
  UserAccountListing,
  UserListingRoleAssignment,
} from '../../swagger'
import { useNavigate } from 'react-router'
import {
  dateToDashString,
  IS_UTC,
  isFirstDateStringBeforeSecond,
} from '../../utils/dateUtility'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { Action } from '../Table/RowActions'
import {
  GridCallbackDetails,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import { Typography } from '@mui/material'
import UserAvatar from '../Elements/UserAvatar'

interface TeamTableProps {
  support: UserAccountListing[]
  /**
   * Update the information surrounding user selection: userId, full name of user, and actorKey of the row in our downline.
   * The presence of this method also lets us know if this table is a 'People I Support' or 'My Support' variant.
   */
  updateUserSelection?: (opts: {
    userKey: number
    name: string
    actorKey: number
    role: string
  }) => void
  ariaLabelledBy?: string
  textTypesForRoles: (role: UserListingRoleAssignment) => React.CSSProperties
  totalCount?: number
  pageSize?: number
  isPaginated?: boolean
  isClickable?: boolean
  handleRowsPerPage: (rowAmount: number) => void
  tableFooterPage: number
  handlePaginationChange?: (_pagination: PaginationResponse) => void
  checkboxSelection?: boolean
  onSelectionModelChange?: (
    newSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void
  selectionModel?: GridRowSelectionModel
}

interface TeamTableActionableRow {
  username: string
  userKey: number
  roleName: string
  role: UserListingRoleAssignment
  startDate: string
  endDate: string
  fullName: string
  actorKey?: number
  id: string
}

export const TeamTable: React.FC<TeamTableProps> = ({
  support,
  updateUserSelection,
  ariaLabelledBy,
  textTypesForRoles,
  totalCount,
  pageSize,
  isPaginated,
  isClickable = true,
  tableFooterPage,
  handlePaginationChange,
  checkboxSelection = false,
  onSelectionModelChange,
  selectionModel,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const actionableTableColumns: ActionableTableColumn[] = [
    {
      fieldName: 'avatar',
      renderCell: ({ row }: GridRenderCellParams<TeamTableActionableRow>) => {
        return <UserAvatar familyName={row.fullName} />
      },
      columnHeaderName: t('Team.Header.Avatar', ''),
      maxWidth: 75,
    },
    {
      fieldName: 'fullName',
      columnHeaderName: t('Team.Header.FullName', 'Full Name'),
    },
    {
      fieldName: 'username',
      columnHeaderName: t('Team.Header.Email', 'Email'),
    },
    {
      fieldName: 'roleName',
      columnHeaderName: t('Team.Header.Role', 'Role'),
      renderCell: ({ row }: GridRenderCellParams<TeamTableActionableRow>) => {
        return (
          <Typography
            style={textTypesForRoles(row.role)}
            variant="body1"
            component="p"
          >
            {row.role.name}
          </Typography>
        )
      },
    },
    //Show role start and end date for people I Support table
    ...((ariaLabelledBy && ariaLabelledBy === 'peopleISupport'
      ? [
          {
            fieldName: 'startDate',
            columnHeaderName: t('Team.Header.RoleStartDate', 'Start Date'),
          },
          {
            fieldName: 'endDate',
            columnHeaderName: t('Team.Header.RoleEndDate', 'End Date'),
          },
        ]
      : []) as ActionableTableColumn[]),
  ]

  const rowActions: Action<TeamTableActionableRow>[] = isClickable
    ? [
        {
          actionName: t('Team.Table.Actions.ViewProfile', 'View Profile'),
          actionKey: `viewProfile`,
          actionFunction: (row) => onClickShowProfile(row),
        },
        {
          actionName: t('Team.Table.Actions.ShowTeam', 'Show Team'),
          actionKey: `showTeam`,
          actionFunction: (row) => onClickShowTeam(row),
        },
      ]
    : []

  const navigate = useNavigate()

  const onClickShowTeam = (row: TeamTableActionableRow) => {
    // Reset page to first when clicking a new actor/user
    updateUserSelection?.({
      userKey: row.userKey,
      name: row.fullName,
      actorKey: row.actorKey ?? -1,
      role: row.roleName,
    })
  }

  const onClickShowProfile = (row: TeamTableActionableRow) => {
    navigate(`/admin/user-accounts/${row.userKey}`, {
      /** Navigation options, as applicable */
    })
  }

  const rows: TeamTableActionableRow[] = support.flatMap((row, rowIndex) =>
    row.roles.map((role, roleIndex) => {
      const isActorKeyDuplicate =
        role.actorKey !== undefined &&
        support.some(
          (otherRow) =>
            otherRow !== row &&
            otherRow.roles.some(
              (otherRole) => otherRole.actorKey === role.actorKey
            )
        )

      return {
        username: row.username,
        userKey: row.userKey,
        actorKey: role.actorKey,
        role,
        roleName: role.name,
        roles: row.roles,
        startDate: dateToDashString(role.validFrom, IS_UTC),
        endDate: dateToDashString(role.validTo, IS_UTC),
        fullName: fullName({
          firstName: row.firstName,
          lastName: row.lastName,
        }),
        // There really should never be undefined or duplicate actorKeys returned from the BE
        id:
          role.actorKey !== undefined
            ? isActorKeyDuplicate
              ? `${role.actorKey}-u${rowIndex}-r${roleIndex}`
              : `${role.actorKey}`
            : `${row.userKey}-${row.roles.indexOf(role)}`,
      }
    })
  )

  const handleSelectionModelChange = (
    newRowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (onSelectionModelChange) {
      onSelectionModelChange(newRowSelectionModel, details)
    }
  }

  // Function to check if a row's validTo date is after the current date
  const isRowValid = (params: GridRowParams) => {
    if (!checkboxSelection) return true

    const row = params.row as TeamTableActionableRow

    // Get the validTo date from the row
    const validToDate = row.role.validTo
    const currentDate = new Date()

    // Check if validTo is after current date
    return isFirstDateStringBeforeSecond(currentDate, validToDate)
  }

  return (
    <ActionableTable
      columns={actionableTableColumns}
      rows={rows}
      noResultsMessage={t('TeamTable.Empty', 'No Results Found')}
      rowActions={rowActions}
      customCssTable={{
        margin: theme.spacing(3, 0),
      }}
      pagination={
        isPaginated
          ? {
              page: tableFooterPage,
              pageSize: pageSize ?? 10,
              totalCount: totalCount ?? 10,
              orderBy: [{}],
            }
          : undefined
      }
      rowHeight={70}
      checkboxSelection={checkboxSelection}
      isRowSelectable={isRowValid}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleSelectionModelChange}
      handlePaginationChange={handlePaginationChange}
    />
  )
}

export default TeamTable
